import { px, TCoreInputOptions, useThemeHook } from '@core/widgets';
import { ConditionalRender } from '@libs/core';
import { AssetsSvgMap, TCommonData, TInputData, TWidget } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, MouseEventHandler } from 'react';
import { IMargin } from '../../../interface/interface';
import { getSemanticColors, SemanticColor, TColorVarsMap, toColorVars } from '../../../theme/colors';
import { getInlineStyles } from '../../../utils/styles';
import { ButtonCircleView } from '../../Buttons/ButtonCircleView';
import styles from './inputWrapper.module.css';

interface IInputWrapperProps extends IMargin {
    widget: TWidget<TInputData>;
    /** Design version to draw in; defaults to `widget.version`. For callers without a real widget. */
    version?: number;
    dataTest?: string;
    isError?: boolean;
    isDisabled?: boolean;
    isHidden?: boolean;
    errorMessage?: string;
    helperText?: string;
    isFilled?: boolean;
    isFocused?: boolean;
    isDropdownInput?: boolean;
    isLinedInput?: boolean;
    isSearchInput?: boolean;
    htmlFor?: string;
    labelText?: string;
    alignCenter?: boolean;
    inputAlignCenter?: boolean;
    children: React.ReactNode;
    leftInputComponent?: React.ReactNode;
    rightInputComponent?: React.ReactNode;
    setFocus?: MouseEventHandler;
    isShowErrorHintButton?: boolean;
    isShowGreyHintButton?: boolean;
    handleErrorClick?: React.MouseEventHandler;
    handleHintClick?: React.MouseEventHandler;
    options?: IOptions;
    useTheme?: boolean;
}
interface IOptions {
    padding?: string;
    width?: string;
    height?: string;
}

/**
 * Default colors of `inputWrapper.module.css`, from the semantic tokens of the widget's design version.
 * Spread before `buildThemeVars`, so a core theme palette still overrides them.
 */
const INPUT_WRAPPER_COLOR_VARS = {
    '--iw-border-color': SemanticColor.BorderPrimary,
    '--iw-border-color-focus': SemanticColor.IconEmphasised,
    '--iw-border-color-error': SemanticColor.TextError,
    '--iw-border-color-disabled': SemanticColor.BorderPrimary,
    '--iw-background': SemanticColor.BgPrimary,
    '--iw-label-background': SemanticColor.BgPrimary,
    '--iw-input-color': SemanticColor.TextPrimary,
    '--iw-hint-color': SemanticColor.TextSecondary,
    '--iw-error-color': SemanticColor.TextError,
    '--iw-disabled-color': SemanticColor.TextPrimaryDisabled,
} as const satisfies TColorVarsMap;

const buildThemeVars = (p: TCoreInputOptions): React.CSSProperties => {
    const vars: Record<string, string | number | undefined> = {
        '--iw-border-color': p.outline,
        '--iw-border-color-focus': p.main,
        '--iw-border-color-error': p.error?.outline,
        '--iw-border-color-disabled': p.onSurface,
        '--iw-input-color': p.onSurface,
        '--iw-hint-color': p.onSurfaceVariant,
        '--iw-error-color': p.error?.onSurfaceVariant,
        '--iw-disabled-color': p.onSurface,
        '--iw-font-family': p.font,
        '--iw-font-size': px(p.fontStyle?.fontSize),
        '--iw-font-weight': p.fontStyle?.fontWeight,
        '--iw-line-height': px(p.fontStyle?.lineHeight),
        '--iw-letter-spacing': px(p.fontStyle?.letterSpacing),
        '--iw-border-radius': px(p.inputBorderRadius),
    };
    return Object.fromEntries(Object.entries(vars).filter(([, v]) => v !== undefined)) as React.CSSProperties;
};

const toColor = (raw?: string, fallback?: string): string => raw ?? (fallback?.startsWith('var(') ? fallback : `var(--${fallback})`);

const InputWrapper: React.FC<IInputWrapperProps> = (props) => {
    const { palette: themePalette } = useThemeHook(props.widget as TWidget<TCommonData, TCoreInputOptions>);
    const palette = props.useTheme ? themePalette : {};
    const version = props.version ?? props.widget?.version;
    const semanticColors = getSemanticColors(version);

    const getLabelColor = (): string => {
        if (props.isDisabled) return toColor(palette.onSurface, semanticColors[SemanticColor.TextPrimaryDisabled]);
        if (!props.isFocused && !props.isError) return toColor(palette.onSurfaceVariant, semanticColors[SemanticColor.TextSecondary]);
        if (props.isFocused && !props.isError) return toColor(palette.main, semanticColors[SemanticColor.TextEmphasised]);
        if (props.isError) return toColor(palette.error?.onSurfaceVariant, semanticColors[SemanticColor.TextError]);
        return toColor(palette.onSurfaceVariant, semanticColors[SemanticColor.TextSecondary]);
    };

    const getPadding = (): string => {
        const pp = palette.padding;
        if (pp && Object.keys(pp).length > 0) {
            return `${pp.top ?? 0}px ${pp.right ?? 0}px ${pp.bottom ?? 0}px ${pp.left ?? 0}px`;
        }
        if (props.options?.padding) {
            return props.options.padding;
        } else if (props.isLinedInput) {
            return '0 0 6px 0';
        } else if (props.rightInputComponent) {
            return '16px 40px 16px 16px';
        } else if (props.isSearchInput) {
            return '16px 24px';
        } else {
            return '16px';
        }
    };

    const getFloatingLabelPosition = (): string => {
        if (props.isLinedInput) {
            return props.isFilled || props.isFocused ? '-4px' : '24px';
        } else {
            return props.isFilled || props.isFocused ? '-12px' : '16px';
        }
    };

    return (
        <Activity mode={props.isHidden ? 'hidden' : 'visible'}>
            <div
                data-test={props.dataTest}
                className={clsx(styles.inputWrapper, props.isSearchInput && styles.searchInputContainer)}
                style={{
                    ...toColorVars(INPUT_WRAPPER_COLOR_VARS, version),
                    ...buildThemeVars(palette),
                    ...getInlineStyles({
                        margin: {
                            top: palette.margin?.top ?? props.marginTop,
                            right: palette.margin?.right ?? props.marginRight,
                            bottom: palette.margin?.bottom ?? props.marginBottom,
                            left: palette.margin?.left ?? props.marginLeft,
                        },
                    }),
                }}
            >
                <div
                    className={clsx(
                        styles.inputContainer,
                        ((!props.isFocused && props.isError) || props.isError) && styles.inputContainerError,
                        props.isFocused && styles.inputContainerFocused,
                        props.isDisabled && styles.inputContainerDisabled,
                        props.isDisabled && styles.inputContainerReadOnly,
                        props.isLinedInput && styles.inputContainerLinedInput,
                        props.isSearchInput && styles.inputContainerSearchInput,
                    )}
                >
                    <label
                        className={clsx(
                            styles.inputLabel,
                            props.isDisabled && styles.disabledColor,
                            props.isDropdownInput && !props.isDisabled && styles.inputLabelDropdownInput,
                            props.isSearchInput && styles.inputLabelSearchInput,
                            props.inputAlignCenter && styles.inputLabelInputAlignCenter,
                            props.isLinedInput && styles.inputLabelLinedInput,
                        )}
                        style={getInlineStyles({ variables: { padding: getPadding() }, width: props.options?.width, height: props.options?.height })}
                        htmlFor={props.htmlFor}
                        onClick={(e) => {
                            if (!props.isDisabled) {
                                props.setFocus?.(e);
                            }
                        }}
                    >
                        {props.leftInputComponent}
                        {props.children}
                        <ConditionalRender condition={!!props.labelText}>
                            <span
                                className={clsx(
                                    styles.floatingLabel,
                                    (props.isFilled || props.isFocused) && styles.floatingLabelFilledOrFocused,
                                    props.isLinedInput && styles.floatingLabelLinedInput,
                                )}
                                style={{ color: getLabelColor(), ...getInlineStyles({ variables: { top: getFloatingLabelPosition() } }) }}
                            >
                                {props.labelText}
                            </span>
                        </ConditionalRender>
                    </label>
                    {props.rightInputComponent && (
                        <div
                            className={clsx(
                                styles.inputRightElement,
                                props.isLinedInput && styles.isLinedInput,
                                props.isSearchInput && styles.isSearchInput,
                                props.isDropdownInput && !props.isDisabled && styles.inputLabelDropdownInput,
                            )}
                        >
                            {props.rightInputComponent}
                        </div>
                    )}
                </div>
                <ConditionalRender condition={!props.isHidden && (!!props.helperText || (props.isError && !!props.errorMessage))}>
                    <div
                        className={clsx(
                            styles.hintContainer,
                            props.isLinedInput && styles.linedInputForHintContainer,
                            props.isError && styles.errorColor,
                            props.isDisabled && styles.disabledColor,
                        )}
                    >
                        {props.isError && !!props.errorMessage ? props.errorMessage : props.helperText}
                        <ConditionalRender condition={!props.isError && props.isShowGreyHintButton}>
                            <div className={styles.hintButton}>
                                <ButtonCircleView
                                    version={version}
                                    size="small"
                                    iconName={AssetsSvgMap.ic_info_circle_small}
                                    iconColor={semanticColors[SemanticColor.IconSecondary]}
                                    hierarchy="secondary"
                                    boxShadow={false}
                                    iconHeight={16}
                                    iconWidth={16}
                                    onClick={props.handleHintClick}
                                />
                            </div>
                        </ConditionalRender>
                        <ConditionalRender condition={props.isError && props.isShowErrorHintButton}>
                            <div className={styles.hintButton}>
                                <ButtonCircleView
                                    version={version}
                                    size="small"
                                    boxShadow={false}
                                    iconName={AssetsSvgMap.ic_info_circle_small}
                                    hierarchy="destructive"
                                    iconHeight={16}
                                    iconWidth={16}
                                    onClick={props.handleErrorClick}
                                />
                            </div>
                        </ConditionalRender>
                    </div>
                </ConditionalRender>
            </div>
        </Activity>
    );
};
export { InputWrapper };
